import type { CSSProperties, ReactNode } from 'react';

/** The mockup's line icons (24-unit grid, round caps), by name. */
const P: Record<string, string> = {
  back: 'M15 5l-7 7 7 7',
  arrow: 'M5 12h14M13 6l6 6-6 6',
  chevronDown: 'M6 9l6 6 6-6',
  chevronRight: 'M9 6l6 6-6 6',
  chevronRight2: 'M9 5l7 7-7 7',
  pin: 'M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 1113 0c0 5.4-6.5 11-6.5 11z',
  search: 'M20 20l-4.2-4.2',
  clock: 'M12 7.5V12l3 2',
  briefcase: 'M9 7V5.5A1.5 1.5 0 0110.5 4h3A1.5 1.5 0 0115 5.5V7M3.5 12.5h17',
  shield: 'M12 3l7.5 3v5.5c0 4.5-3.2 8.2-7.5 9.5-4.3-1.3-7.5-5-7.5-9.5V6z',
  shieldCheck: 'M8.8 12.2l2.2 2.2 4.2-4.4',
  shield2: 'M12 3.5l7 2.6v5.3c0 4.4-3 7.8-7 9.1-4-1.3-7-4.7-7-9.1V6.1z',
  shield2Check: 'M9 12l2.2 2.2L15.5 10',
  home: 'M4 10.5L12 4l8 6.5V19a1 1 0 01-1 1h-4.5v-5.5h-5V20H5a1 1 0 01-1-1z',
  close: 'M6 6l12 12M18 6L6 18',
  phone: 'M6.6 3.5h2.6l1.6 4.2-2 1.3a11 11 0 006.2 6.2l1.3-2 4.2 1.6v2.6a2 2 0 01-2.2 2A16.5 16.5 0 014.6 5.7a2 2 0 012-2.2z',
  chat: 'M20 11.5a7.5 7 0 01-11.2 6.1L4.5 19l1.4-3.9A7 7 0 1120 11.5z',
  mail: 'M5.5 6h13a1.5 1.5 0 011.5 1.5v9a1.5 1.5 0 01-1.5 1.5h-13A1.5 1.5 0 014 16.5v-9A1.5 1.5 0 015.5 6zM4.5 7l7.5 6 7.5-6',
  share: 'M12 3.5v11M7.5 8L12 3.5 16.5 8M5 12.5V18a2 2 0 002 2h10a2 2 0 002-2v-5.5',
  lock: 'M8.5 10.5V8a3.5 3.5 0 017 0v2.5',
  check: 'M5 12.5l4.5 4.5L19 7.5',
  help: 'M9.6 9.4a2.5 2.5 0 014.8 1c0 1.7-2.4 2.1-2.4 3.4M12 16.8v.2',
  edit: 'M4 20l4.5-1 10-10a2.1 2.1 0 00-3-3l-10 10z',
  edit2: 'M13.5 7.5l3 3',
  globe: 'M3.5 12h17M12 3.5c2.5 2.6 3.6 5.3 3.6 8.5s-1.1 5.9-3.6 8.5c-2.5-2.6-3.6-5.3-3.6-8.5s1.1-5.9 3.6-8.5z',
  note: 'M5 4.5h14v10.5l-4.5 4.5H5z',
  note2: 'M14.5 19.5V15H19M8.5 9h7M8.5 12.5h4',
  bank: 'M4 9.5L12 5l8 4.5M5.5 10v7.5M18.5 10v7.5M9.5 10v7.5M14.5 10v7.5M3.5 19.5h17',
  locate: 'M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3',
  cashLines: 'M6.5 9.8v4.4M17.5 9.8v4.4',
  ride: 'M8.2 18.5H15a3.25 3.25 0 000-6.5H9a3.25 3.25 0 010-6.5h6.8',
  history1: 'M4 12a8 8 0 102.3-5.6',
  history2: 'M4 4.5v4h4',
  history3: 'M12 8v4.3l2.8 1.7',
  user: 'M4.8 20c.9-3.6 3.8-5.7 7.2-5.7s6.3 2.1 7.2 5.7',
  wheel: 'M3.8 10.6l6 1M14.2 11.6l6-1M12 14.3v6.2',
  qr: 'M13.5 13.5h3v3M20 13.5v.01M16.5 20h3.5v-3.5M13.5 20v-3',
  plus: 'M12 5v14M5 12h14',
  trash: 'M5 7h14M10 7V5h4v2M7 7l1 12h8l1-12',
  moon: 'M20 14.5A8 8 0 019.5 4a8 8 0 1010.5 10.5z',
};

export type IconName =
  | 'back' | 'arrow' | 'chevronDown' | 'chevronRight' | 'chevronRight2' | 'pin' | 'search' | 'clock' | 'briefcase'
  | 'shield' | 'shield2' | 'home' | 'close' | 'phone' | 'chat' | 'share' | 'lock' | 'check' | 'help' | 'edit'
  | 'globe' | 'note' | 'bank' | 'locate' | 'cash' | 'ride' | 'history' | 'user' | 'wheel' | 'qr' | 'star' | 'plus'
  | 'trash' | 'moon' | 'backspace' | 'info' | 'alert' | 'navigate' | 'mail';

export function Icon({ name, size = 20, sw = 2, style }: { name: IconName; size?: number; sw?: number; style?: CSSProperties }) {
  const common = { fill: 'none', stroke: 'currentColor', strokeWidth: sw, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  let body: ReactNode;
  switch (name) {
    case 'pin':
      body = (<><path d={P.pin} /><circle cx="12" cy="10" r="2.4" /></>);
      break;
    case 'search':
      body = (<><circle cx="11" cy="11" r="6.5" /><path d={P.search} /></>);
      break;
    case 'clock':
      body = (<><circle cx="12" cy="12" r="8.5" /><path d={P.clock} /></>);
      break;
    case 'briefcase':
      body = (<><rect x="3.5" y="7" width="17" height="12.5" rx="2.5" /><path d={P.briefcase} /></>);
      break;
    case 'shield':
      body = (<><path d={P.shield} /><path d={P.shieldCheck} /></>);
      break;
    case 'shield2':
      body = (<><path d={P.shield2} /><path d={P.shield2Check} /></>);
      break;
    case 'lock':
      body = (<><rect x="5" y="10.5" width="14" height="10" rx="2.5" /><path d={P.lock} /></>);
      break;
    case 'help':
      body = (<><circle cx="12" cy="12" r="8.5" /><path d={P.help} /></>);
      break;
    case 'edit':
      body = (<><path d={P.edit} /><path d={P.edit2} /></>);
      break;
    case 'globe':
      body = (<><circle cx="12" cy="12" r="8.5" /><path d={P.globe} /></>);
      break;
    case 'note':
      body = (<><path d={P.note} /><path d={P.note2} /></>);
      break;
    case 'locate':
      body = (<><circle cx="12" cy="12" r="6.5" /><circle cx="12" cy="12" r="1.6" fill="currentColor" /><path d={P.locate} /></>);
      break;
    case 'cash':
      body = (<><rect x="3" y="6.5" width="18" height="11" rx="2.5" /><circle cx="12" cy="12" r="2.5" /><path d={P.cashLines} /></>);
      break;
    case 'ride':
      body = (<><circle cx="6" cy="18.5" r="2.2" /><circle cx="18" cy="5.5" r="2.2" /><path d={P.ride} /></>);
      break;
    case 'history':
      body = (<><path d={P.history1} /><path d={P.history2} /><path d={P.history3} /></>);
      break;
    case 'user':
      body = (<><circle cx="12" cy="8.5" r="3.8" /><path d={P.user} /></>);
      break;
    case 'wheel':
      body = (<><circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="2.3" /><path d={P.wheel} /></>);
      break;
    case 'qr':
      body = (<><rect x="4" y="4" width="6.5" height="6.5" rx="1.2" /><rect x="13.5" y="4" width="6.5" height="6.5" rx="1.2" /><rect x="4" y="13.5" width="6.5" height="6.5" rx="1.2" /><path d={P.qr} /></>);
      break;
    case 'info':
      body = (<><circle cx="12" cy="12" r="8.5" /><path d="M12 11v5M12 8v.01" /></>);
      break;
    case 'alert':
      body = (<><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5v5.5M12 16.2v.01" /></>);
      break;
    case 'navigate':
      body = <path d="M4 11l16-7-7 16-2-7z" />;
      break;
    case 'backspace':
      body = (<><path d="M9 5h10a1.5 1.5 0 011.5 1.5v11A1.5 1.5 0 0119 19H9l-6-7z" /><path d="M12 9.5l5 5M17 9.5l-5 5" /></>);
      break;
    case 'star':
      return (
        <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" style={{ flex: 'none', ...style }}>
          <path d="M12 3.6l2.5 5.2 5.7.8-4.1 4 1 5.6L12 16.6l-5.1 2.6 1-5.6-4.1-4 5.7-.8z" fill="currentColor" />
        </svg>
      );
    default:
      body = <path d={P[name]} />;
  }
  return (
    <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" {...common} style={style}>
      {body}
    </svg>
  );
}
